import { FunctionService } from 'src/app/services/general/function.service';
import { TransactionType } from 'src/app/types/transaction';

import { Platform } from '@ionic/angular';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { AccountService } from 'src/app/services/account/account.service';
import { TransactionService } from 'src/app/services/payment/transaction.service';
import { PaymentRef, RefundTransaction, Transaction } from 'src/app/interfaces/transaction';
import { ReceiptService } from 'src/app/services/pdf/receipt.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DateTimeService } from 'src/app/services/general/date-time.service';
import { RefundService } from 'src/app/services/payment/refund.service';

@Component({
  selector: 'app-user-transaction',
  templateUrl: './user-transaction.page.html',
  styleUrls: ['./user-transaction.page.scss'],
})
export class UserTransactionPage implements OnInit, OnDestroy {

  @ViewChild(CdkVirtualScrollViewport) cdkVirtualScrollViewport: CdkVirtualScrollViewport;
  
  transactionType: TransactionType;
  /**
   * Transaction list
   */
  transactionList: Transaction[];

  refundList: { [transactionId: string]: RefundTransaction[] };

  transactionAmount: { [transactionId: string]: number };
  /**
   * Account ID
   */
  accountId: string;
  /**
   * User transaction subscription
   */
  private userTransactionSubscription: Subscription;

  private refundSubscription: Subscription;

  /**
   * Constructor
   * @param router router
   * @param transactionService transaction service
   */
  constructor(
    private platform: Platform,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private dateTimeService: DateTimeService,
    private transactionService: TransactionService,
    private refundService: RefundService,
    private receiptService: ReceiptService,
    private functionService: FunctionService,
  ) { }

  ngOnInit(): void {
    this.watchRoute();
  }

  ngOnDestroy(): void {
    this.unwatchUserTransaction();
  }

  /**
   * Watch route
   */
  watchRoute() {
    this.route.params.subscribe((param: Params) => {
      const route = this.router.getCurrentNavigation();
      const state = route?.extras?.state;
      if (param?.transactionId) {
        this.openReceipt(param.transactionId);
      } else if (state?.transactionId) {
        this.openReceipt(state.transactionId);
      }
    });
  }

  /**
   * Before view enter
   */
  async ionViewWillEnter() {
    this.accountId = await this.accountService.readAccountId();

    if (!this.transactionType) {
      this.transactionType = 'account';
    }

    this.watchUserTransaction();
    this.watchRefund();
  }

  /**
   * Before view leave
   */
  ionViewWillLeave() {
    this.unwatchUserTransaction();
    this.unwatchRefund();
  }

  /**
   * Watch user transaction
   */
  async watchUserTransaction() {
    if (!this.userTransactionSubscription) {
      this.userTransactionSubscription = this.transactionService.observableTransactionList.subscribe(() => {
        this.setupTransactionList();
        // this.setupTransactionAmount();
      });
    }
    
  }

  /**
   * Unwatch user transaction
   */
  async unwatchUserTransaction() {
    if (this.userTransactionSubscription) {
      this.userTransactionSubscription.unsubscribe();
      this.userTransactionSubscription = null;
    }
  }

  async watchRefund() {
    if (!this.refundSubscription) {
      this.refundSubscription = this.refundService.observableRefundList.subscribe((refundList: RefundTransaction[]) => {
        this.setupRefundList(refundList);
        // this.setupTransactionAmount();
      });
    }
  }

  async unwatchRefund() {
    if (this.refundSubscription) {
      this.refundSubscription.unsubscribe();
      this.refundSubscription = null;
    }
  }

  setupViewport() {
    if (this.cdkVirtualScrollViewport) {
      setTimeout(() => {
        this.cdkVirtualScrollViewport?.checkViewportSize();
      }, 200);
    } else {
      setTimeout(() => {
        this.setupViewport();
      }, 200);
    }
  }

  setupRefundList(refundList: RefundTransaction[]) {
    if (!this.refundList) {
      this.refundList = {};
    }
    if (refundList?.length) {
      refundList.forEach((x: RefundTransaction) => {
        if (x?.transactionId) {
          if (!this.refundList?.[x.transactionId]) {
            this.refundList[x.transactionId] = [];
          }
          const index = this.refundList[x.transactionId].findIndex((y: RefundTransaction) => {
            return y.refundId === x.refundId;
          });
          if (index !== -1) {
            this.refundList[x.transactionId][index] = x;
          } else {
            this.refundList[x.transactionId].push(x);
          }
        }
      });
    }
    this.setupTransactionAmount();
  }

  setupTransactionAmount() {
    if (!this.transactionAmount) {
      this.transactionAmount = {};
    }

    this.transactionList?.forEach((x: Transaction) => {
      if (x?.transactionId) {
        this.transactionAmount[x.transactionId] = this.getTransactionAmount(x);
      }
    });
  }

  openReceipt(transactionId: string) {
    const transaction = this.transactionService.getTransactionById(transactionId);
    if (transactionId && transaction?.transactionId === transactionId) {
      this.presentUserReceiptModal(transaction);
    }
  }

  format(timestamp: number, dateTimeFormat?: string, timezone?: string) {
    return this.dateTimeService.format(timestamp, dateTimeFormat, '', timezone);
  }

  /**
   * Search transaction
   */
  setupTransactionList() {
    this.transactionList = this.transactionService.getTransactionList(this.transactionType);
    this.setupTransactionAmount();
    this.setupViewport();
  }

  /**
   * Get card No
   * @param paymentRef payment reference
   * @returns card no
   */
  // getCardNo(paymentRef: PaymentRef): string {
  //   if (paymentRef?.method ) {
  //     if (paymentRef?.detail) {
  //       const detail: StripeData = paymentRef.detail as StripeData;
  //       if (detail?.card) {
  //         return detail.card;
  //       }
  //     }
  //   }
  //   return '';
  // }

  getFpxDetail(fpx: string): string {
    return this.functionService.replaceAll(fpx, '_', ' ')?.toUpperCase();
  }

  getPaymentMethod(paymentRef: PaymentRef): string {
    return this.transactionService.getPaymentMethod(paymentRef);
  }

  getTransactionAmount(transaction: Transaction): number {
    return this.transactionService.getTransactionAmount(transaction);
  }

  getRefundList(transactionId: string): RefundTransaction[] {
    return this.refundService.getRefundListByTransactionId(transactionId);
  }

  purchase() {
    if (this.transactionType === 'account') {
      this.goSubscriptionAccountPage();
    } else if (this.transactionType === 'blast') {
      this.goBlastHomePage();
    }
  }

  /**
   * Go user profile page
   */
  goUserProfilePage() {
    this.router.navigate(['/user/profile']);
  }

  /**
   * Go user receipt page
   * @param transaction transaction
   */
  presentUserReceiptModal(transaction: Transaction) {
    this.receiptService.presentUserReceiptModal(transaction);
  }

  /**
   * Go subscription account page
   */
  goSubscriptionAccountPage() {
    const navigationExtras: NavigationExtras = {
      state: {}
    };
    if (this.platform.is('ios') && this.platform.is('hybrid')) {
      navigationExtras.state.backPremiumPage = true;
      this.router.navigate(['/subscription/premium'], navigationExtras);
    } else {
      this.router.navigate(['/subscription/account'], navigationExtras);
    }
  }

  goBlastHomePage() {
    this.router.navigate(['/blast/home']);
  }

  /**
   * Calculate item height for virtual scroll
   * @param item Item
   */
  itemHeightFn() {
    return 120;
  }

  itemHeight(transaction: Transaction) {
    const item = 120;
    const refundHeight = 20;

    const refundList = this.getRefundList(transaction?.transactionId);
    const height = item + (refundList.length * refundHeight);
    return height;
  }

  /**
   * Track item
   * @param index Index
   * @param item item
   */
  trackByFn(index: number, item: Transaction) {
    if (item?.transactionId) {
      return item.transactionId;
    }
    return index;
  }
}
